// Calendar period state for the monthly/weekly/daily posts views (feature 007-monthly-posts-view).
// Owns the active view + cursor date, derives the visible date range (Monday-start weeks),
// and persists view/cursor across reloads via the existing calendar localStorage helpers.
import {useCallback, useMemo, useState} from "react";
import {
    addDays,
    addMonths,
    addWeeks,
    endOfDay,
    endOfMonth,
    endOfWeek,
    startOfDay,
    startOfMonth,
    startOfWeek,
} from "date-fns";
import {
    getCalendarStartDate,
    getCalendarView,
    setCalendarStartDate,
    setCalendarView,
} from "../../helpers/functions";

export type CalendarViewId = "dayGridMonth" | "timeGridWeek" | "timeGridDay";

export interface CalendarRange {
    start: Date;
    end: Date;
}

export interface UseCalendarPeriod {
    viewId: CalendarViewId;
    cursor: Date;
    range: CalendarRange;
    prev(): void;
    next(): void;
    today(): void;
    gotoDate(d: Date): void;
    changeView(v: CalendarViewId): void;
    scrollTarget?: Date;
}

const WEEK_OPTS = {weekStartsOn: 1 as const}; // Monday-start weeks.
const DEFAULT_VIEW: CalendarViewId = "dayGridMonth";
const KNOWN_VIEWS: CalendarViewId[] = ["dayGridMonth", "timeGridWeek", "timeGridDay"];

function readInitialView(): CalendarViewId {
    const stored = getCalendarView();
    return stored && (KNOWN_VIEWS as string[]).includes(stored) ? (stored as CalendarViewId) : DEFAULT_VIEW;
}

function readInitialCursor(): Date {
    const stored = getCalendarStartDate();
    if (stored) {
        const d = new Date(stored);
        if (!isNaN(d.getTime())) return d;
    }
    return new Date();
}

function computeRange(viewId: CalendarViewId, cursor: Date): CalendarRange {
    switch (viewId) {
        case "dayGridMonth":
            return {
                start: startOfWeek(startOfMonth(cursor), WEEK_OPTS),
                end: endOfWeek(endOfMonth(cursor), WEEK_OPTS),
            };
        case "timeGridWeek":
            return {
                start: startOfWeek(cursor, WEEK_OPTS),
                end: endOfWeek(cursor, WEEK_OPTS),
            };
        case "timeGridDay":
        default:
            return {
                start: startOfDay(cursor),
                end: endOfDay(cursor),
            };
    }
}

export function useCalendarPeriod(): UseCalendarPeriod {
    const [viewId, setViewIdState] = useState<CalendarViewId>(readInitialView);
    const [cursor, setCursorState] = useState<Date>(readInitialCursor);

    const applyView = useCallback((v: CalendarViewId) => {
        setViewIdState(v);
        setCalendarView(v);
    }, []);

    const applyCursor = useCallback((d: Date) => {
        setCursorState(d);
        setCalendarStartDate(d.toISOString());
    }, []);

    const range = useMemo(() => computeRange(viewId, cursor), [viewId, cursor]);

    const prev = useCallback(() => {
        setCursorState((c) => {
            const d = viewId === "dayGridMonth" ? addMonths(c, -1)
                : viewId === "timeGridWeek" ? addWeeks(c, -1)
                    : addDays(c, -1);
            setCalendarStartDate(d.toISOString());
            return d;
        });
    }, [viewId]);

    const next = useCallback(() => {
        setCursorState((c) => {
            const d = viewId === "dayGridMonth" ? addMonths(c, 1)
                : viewId === "timeGridWeek" ? addWeeks(c, 1)
                    : addDays(c, 1);
            setCalendarStartDate(d.toISOString());
            return d;
        });
    }, [viewId]);

    const today = useCallback(() => {
        applyCursor(new Date());
    }, [applyCursor]);

    const gotoDate = useCallback((d: Date) => {
        applyCursor(d);
    }, [applyCursor]);

    const changeView = useCallback((v: CalendarViewId) => {
        applyView(v);
    }, [applyView]);

    return {viewId, cursor, range, prev, next, today, gotoDate, changeView};
}
